import { useCallback, useEffect, useMemo, useState } from 'react';
import { RoutesService } from '../../../../services/routes.service';
import { useSession } from '../../../../hooks/use-session.hook';
import { usePermissions } from '../../../../hooks/use-permissions.hook';
import { PROFILES } from '../../../../constants';
import { AlertService } from '../../../../services/alert.service';
import {
  FilterDef,
  FilterOption,
  FilterValues
} from '../../../../components/chip-filters/chip-filters.types';
import useRequest from '../../../../hooks/use-request.hook';
import useBgPages from '../../../../hooks/use-bg-pages.hook';
import Icon from '../../../../components/forms/icon.component';
import Aside from '../../../../components/layout/asaid.component';
import Header from '../../../../components/layout/header.component';
import Footer from '../../../../components/layout/footer.component';
import FilterBar from '../../../../components/chip-filters/filter-bar.component';
import ModuleStatsHeader from '../../../../components/cards/header-stats.component';
import CampaignEffectivenessTable from './table-outgoing';
import OpensipsEntityModal from '../../opensips/opensips-entity.modal';
import OpensipsReloadButton from '../../opensips/opensips-reload.button';
import CapacityModal from '../../opensips/capacity.modal';

const RoutesOutgoing: React.FC = () => {
  useBgPages();
  const { session } = useSession();
  const { call, isRequesting } = useRequest();
  const { isAdmin, can } = usePermissions();
  const canWrite = can('opensips.write');
  const canReload = can('opensips.reload.lb');
  const isRoot = session?.profile === PROFILES.SUPER_ADMIN;

  const [routesRaw, setRoutesRaw] = useState<any[]>([]);
  const [filters, setFilters] = useState<FilterValues>({});
  const [showModal, setShowModal] = useState<{ show: boolean; data: any; duplicate: boolean }>({
    show: false,
    data: null,
    duplicate: false
  });
  const [capacity, setCapacity] = useState<{ show: boolean; data: any }>({
    show: false,
    data: null
  });

  const routeRows = useMemo<any[]>(() => {
    if (!Array.isArray(routesRaw)) return [];
    return routesRaw.map((item: any) => {
      const formattedResources = Array.isArray(item.resources)
        ? item.resources.map((r: any) => `${r.resource?.toUpperCase()} (${r.capacity})`).join(', ')
        : '—';
      const resourceTypes = Array.isArray(item.resources)
        ? item.resources.map((r: any) => String(r.resource).toLowerCase())
        : [];

      return {
        id: item.id,
        groupId: item.groupId ?? 0,
        dstUri: item.dstUri || '—',
        probeMode: item.probeMode ?? 0,
        description: item.description || 'Sin descripción',
        maxChannels: item.maxChannels ?? 'Sin límite',
        enabled: item.active ?? false,
        resourceList: formattedResources,
        resourceTypes,
        company: item.groupId ? String(item.groupId) : '0',
        raw: item
      };
    });
  }, [routesRaw]);

  const groupOptions = useMemo<FilterOption[]>(() => {
    const seen = new Set<string>();
    return routeRows.reduce<FilterOption[]>((acc, r) => {
      const gId = String(r.groupId);
      if (gId && !seen.has(gId)) {
        seen.add(gId);
        acc.push({ value: gId, label: `GRUPO ${gId}` });
      }
      return acc;
    }, []);
  }, [routeRows]);

  const statusOptions = useMemo<FilterOption[]>(() => {
    return [
      { value: 'true', label: 'ACTIVO' },
      { value: 'false', label: 'INACTIVO' }
    ];
  }, []);

  const resourceOptions = useMemo<FilterOption[]>(() => {
    const seen = new Set<string>();
    const options: FilterOption[] = [];
    routeRows.forEach((r) => {
      r.resourceTypes.forEach((type: string) => {
        if (!seen.has(type)) {
          seen.add(type);
          const label = type === 'healt' ? 'HEALTH' : type.toUpperCase();
          options.push({ value: type, label });
        }
      });
    });
    return options;
  }, [routeRows]);

  const filterDefs = useMemo<FilterDef[]>(() => {
    const defs: FilterDef[] = [];

    if (groupOptions.length > 0) {
      defs.push({
        key: 'company',
        label: 'Grupo',
        icon: <Icon icon="ki-sliders" size="fs-7" />,
        options: groupOptions,
        multi: false,
        searchable: true
      });
    }

    if (resourceOptions.length > 0) {
      defs.push({
        key: 'resource',
        label: 'Recurso',
        icon: <Icon icon="ki-layers" size="fs-7" />,
        options: resourceOptions,
        multi: false,
        searchable: false
      });
    }

    defs.push({
      key: 'status',
      label: 'Estado',
      icon: <Icon icon="ki-shield-search" size="fs-7" />,
      options: statusOptions,
      multi: false,
      searchable: false
    });

    return defs;
  }, [groupOptions, resourceOptions, statusOptions]);

  const displayRows = useMemo(() => {
    let rows = routeRows;

    if (filters.company) {
      rows = rows.filter((r) => String(r.groupId) === filters.company);
    }

    if (filters.resource) {
      const selectedResource = String(filters.resource).toLowerCase();
      rows = rows.filter((r) => r.resourceTypes.includes(selectedResource));
    }

    if (filters.status) {
      const targetStatus = filters.status === 'true';
      rows = rows.filter((r) => r.enabled === targetStatus);
    }

    return [...rows].sort((a, b) => a.id - b.id);
  }, [routeRows, filters]);

  const handleFilterChange = useCallback((next: FilterValues) => {
    setFilters(next);
  }, []);

  const fetchRoutes = useCallback(async () => {
    try {
      const response = await call(() => RoutesService.getOutgoing());
      setRoutesRaw(response);
    } catch (error: any) {
      AlertService.toastError(error?.error || error);
    }
  }, [call]);

  const handleDeleteRoute = useCallback(
    async (id: string | number) => {
      try {
        const deletePromise = RoutesService.deleteOutgoing(String(id));

        await AlertService.toastLoading(
          deletePromise,
          'Eliminando registro...',
          'Registro eliminado exitosamente.',
          'Ocurrió un error al intentar eliminar el registro.'
        );
        fetchRoutes();
      } catch (error: any) {
        AlertService.toastError(error?.error || error);
      }
    },
    [fetchRoutes]
  );

  const handleCloseModal = useCallback(
    async (refresh: boolean) => {
      if (refresh) await fetchRoutes();
      setShowModal({ show: false, data: null, duplicate: false });
    },
    [fetchRoutes]
  );

  const handleRefresh = useCallback(async () => {
    await fetchRoutes();
    AlertService.toastSuccess('¡Éxito!, información actualizada correctamente.');
  }, [fetchRoutes]);

  useEffect(() => {
    fetchRoutes();
  }, [fetchRoutes]);

  return (
    <div className="page d-flex flex-row flex-column-fluid">
      <Aside />
      <div className="wrapper d-flex flex-column flex-row-fluid" id="kt_wrapper">
        <Header />
        <div
          className="content fs-6 d-flex flex-column flex-column-fluid bg-cards-p"
          id="kt_content"
        >
          <div className="post fs-6 d-flex flex-column-fluid" id="kt_post">
            <div className="container-fluid px-4 px-md-10 px-xxl-15">
              <ModuleStatsHeader
                stats={[
                  {
                    label: 'Total Enlaces',
                    value: displayRows.length,
                    icon: 'ki-duotone ki-router fs-1 text-primary',
                    bgColor: 'bg-light-primary',
                    textColor: 'text-primary'
                  },
                  {
                    label: 'Enlaces Activos',
                    value: displayRows.filter((r) => r.enabled).length,
                    icon: 'ki-duotone ki-check-circle fs-1 text-primary',
                    bgColor: 'bg-light-primary',
                    textColor: 'text-primary'
                  }
                ]}
                onRefresh={handleRefresh}
                isLoading={isRequesting}
                extraActions={
                  <div className="d-flex flex-wrap gap-2">
                    {canReload && (
                      <OpensipsReloadButton
                        label="Recargar lb"
                        command="lb_reload"
                        onReload={RoutesService.reloadOutgoing}
                      />
                    )}
                    {canWrite && (
                      <button
                        type="button"
                        className="btn btn-primary d-flex align-items-center gap-2 hover-scale fs-7 fw-bold rounded-pill"
                        onClick={() => setShowModal({ show: true, data: null, duplicate: false })}
                      >
                        <Icon icon="ki-plus" size="fs-3" />
                        Agregar salida
                      </button>
                    )}
                  </div>
                }
              />
              {routeRows.length > 0 && (
                <FilterBar
                  className="mt-4"
                  defs={filterDefs}
                  value={filters}
                  onChange={handleFilterChange}
                  channelOptions={[]}
                  selectedChannel=""
                  onChannelChange={() => {}}
                />
              )}
              <div className="card shadow-sm border-0 rounded-4 mt-4 mt-md-6 overflow-hidden">
                <div className="card-body p-4 p-md-6">
                  <CampaignEffectivenessTable
                    rows={displayRows}
                    isLoading={isRequesting}
                    isRoot={isRoot}
                    isAdmin={isAdmin}
                    canWrite={canWrite}
                    onEdit={(row: any) =>
                      setShowModal({ show: true, data: row.raw, duplicate: false })
                    }
                    onDuplicate={(row: any) =>
                      setShowModal({ show: true, data: row.raw, duplicate: true })
                    }
                    onCapacity={(row: any) => setCapacity({ show: true, data: row.raw })}
                    onDelete={handleDeleteRoute}
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
        <Footer />
      </div>

      {showModal.show && (
        <OpensipsEntityModal
          kind="saliente"
          show={showModal.show}
          payload={showModal.data}
          duplicate={showModal.duplicate}
          onRequestClose={handleCloseModal}
        />
      )}
      {capacity.show && capacity.data && (
        <CapacityModal
          show={capacity.show}
          saliente={capacity.data}
          onRequestClose={async (refresh: boolean) => {
            if (refresh) await fetchRoutes();
            setCapacity({ show: false, data: null });
          }}
        />
      )}
    </div>
  );
};

export default RoutesOutgoing;
